import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { forceRenderStyles, style } from "typestyle";
import React, { Component } from "react";
import { isEmpty } from "lodash";
import { Feature } from "geojson";

import MapPolygonEditControl from "../../../../leafletControls/MapPolygonEditControl";
import AccurateControl from "../../../../leafletControls/AccurateControl";
import MapZoomControl from "../../../../leafletControls/Zoom";
import MapBaseLayersControl, {
  OSM_RASTER_LAYER,
  MAPBOX_SATELLITE,
} from "../../../../leafletControls/MapBaseLayersControl";

import AddressSearchInput from "./GeocoderExtension/AddressSearchInput";
import MapControlsLayout from "../../../../components/MapControlsLayout";
import MapContainerWithUiLayers from "../../../../components/MapContainerWithUiLayers";
import { OpencageGeocodeResult } from "./GeocoderExtension/GeocoderService";
import UploadButtonsControl from "../../../../leafletControls/UploadButtonsControl";

interface ActionGeoJsonProps {
  value?: Feature;
  onInputSuccess: (value?: Feature) => void;
  disabled?: boolean;
}

interface ActionGeoJsonState {
  map: L.Map | null;
  isAccurate: boolean;
}

export default class ActionGeoJson extends Component<
  ActionGeoJsonProps,
  ActionGeoJsonState
> {
  state: ActionGeoJsonState = { map: null, isAccurate: false };
  mapContainer = React.createRef<HTMLDivElement>();

  componentDidMount() {
    forceRenderStyles();
    this.setState(
      {
        map: L.map(this.mapContainer.current as HTMLDivElement, {
          zoomControl: false,
          scrollWheelZoom: false,
        }).setView([50, 50], 3),
      },
      () => {
        if (!this.props.value) {
          // pan map to suitable position

          // default to USA
          if (this.state.map != null) {
            this.state.map.setView([40.56, -100.81], 5);

            const onGetCurrentPosition = (geoEv: GeolocationPosition) => {
              if (this.state.map != null) {
                this.state.map.setView(
                  [geoEv.coords.latitude, geoEv.coords.longitude],
                  8,
                );
              }
            };

            navigator.geolocation &&
              navigator.geolocation.getCurrentPosition(
                onGetCurrentPosition,
                () => {},
                {
                  enableHighAccuracy: false,
                  timeout: Infinity,
                  maximumAge: 0,
                },
              );
          }
        }
      },
    );
  }

  handleFeatureChanged = (feature: Feature | null) => {
    if (feature != null && feature.properties != null) {
      feature.properties.isAccurate = this.state.isAccurate;
      this.props.onInputSuccess(feature);
    }
  };

  handleGeocoderResultSelected = (e: OpencageGeocodeResult) => {
    const { geoJSON } = e;
    let bounds;

    if (geoJSON.properties != null && geoJSON.properties.bounds) {
      const { northeast, southwest } = geoJSON.properties.bounds;
      bounds = L.latLngBounds(southwest, northeast);
    } else {
      bounds = L.geoJSON(geoJSON).getBounds();
    }

    if (this.state.map != null) {
      this.state.map.fitBounds(bounds);
    }
  };

  render() {
    const { map } = this.state;
    return (
      <div
        className={style({
          position: "relative",
          opacity: 0.99, // create new stacking context to not allow map overlap other elements (https://philipwalton.com/articles/what-no-one-told-you-about-z-index/)
        })}
        onFocus={() => {
          map && map.scrollWheelZoom.enable();
        }}
        onBlur={() => {
          map && map.scrollWheelZoom.disable();
        }}
      >
        <div
          className={style({
            position: "relative",
            zIndex: 2,
            marginBottom: "8px",
          })}
        >
          <AddressSearchInput
            onGeocoderResultSelected={this.handleGeocoderResultSelected}
          />
        </div>
        <div
          className={style({
            position: "relative",
            zIndex: 1,
            width: "100%",
            minHeight: "16px",
            height: "560px",
          })}
        >
          {this.renderMap()}
        </div>
      </div>
    );
  }

  renderMap() {
    const { map } = this.state;
    return (
      <MapContainerWithUiLayers
        mapElement={
          <div
            className={style({
              // @ts-ignore
              position: "absolute !important",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            })}
            ref={this.mapContainer}
          />
        }
        uiLayers={
          map
            ? [
                <MapControlsLayout
                  key="corner_controls"
                  topCenter={
                    this.props.value && (
                      <MapPolygonEditControl
                        onChange={this.handleFeatureChanged}
                        value={this.props.value}
                        map={map}
                        clearAreaDisabled={true}
                        allowSelfIntersection={false}
                      />
                    )
                  }
                  topLeft={
                    !this.props.value ? (
                      <MapPolygonEditControl
                        onChange={this.handleFeatureChanged}
                        map={map}
                        clearAreaDisabled={true}
                        allowSelfIntersection={false}
                      />
                    ) : null
                  }
                  topRight={
                    <UploadButtonsControl
                      onlyKml={true}
                      onGeoJsonUploaded={(geoJson) =>
                        this.handleFeatureChanged(geoJson)
                      }
                    />
                  }
                  bottomLeft={
                    <MapBaseLayersControl
                      map={map}
                      layers={[MAPBOX_SATELLITE, OSM_RASTER_LAYER]}
                    />
                  }
                />,
                <MapControlsLayout
                  key="center_controls"
                  middleLeft={<MapZoomControl map={map} />}
                  topLeft={
                    <AccurateControl
                      disabled={isEmpty(this.props.value)}
                      isAccurate={this.state.isAccurate}
                      onChange={(v) => {
                        this.setState({ isAccurate: v }, () => {
                          this.handleFeatureChanged(this.props.value || null);
                        });
                      }}
                    />
                  }
                />,
              ]
            : []
        }
      />
    );
  }
}
